import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import {
  ERPPage, ERPPageHeader, ERPKPIGrid, ERPKPICard, ERPButton
} from '../../components/ui/ERPLayout';
import LoncheadosTable from './components/LoncheadosTable';
import LoncheadosDetailModal from './components/LoncheadosDetailModal';
import LoncheadosEditModal from './components/LoncheadosEditModal';
import { clientService } from '../../services/clientService';
import { employeeService } from '../../services/employeeService';
import { productosService } from '../../services/productService';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';
import { exportLoncheadosPDF } from './utils/pdfExportUtils';
import { applyCustomDateRange, applyQuickPeriodFilter } from '../../utils/dateRangeHelper';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const TIMEZONE = 'Europe/Madrid';

// ─── Timezone helpers ─────────────────────────────────────────────────────────
// These are now handled by src/utils/dateRangeHelper.js

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, totalRecords, pageSize, onPageChange, onPageSizeChange }) => {
  const from = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalRecords);
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
      style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
    >
      <div className="flex items-center gap-4">
        <span className="text-sm font-oranienbaum text-white/50">
          Mostrando <span className="text-white/80 font-bold">{from}–{to}</span> de{' '}
          <span className="text-white/80 font-bold">{totalRecords}</span> registros
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-oranienbaum text-white/40">Por página:</span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e?.target?.value))}
            className="font-oranienbaum text-sm text-white/80 rounded-lg px-2 py-1 focus:outline-none"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            {PAGE_SIZE_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(1)} disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">‹ Anterior</button>
        <span className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-bold"
          style={{ background: 'rgba(214,239,78,0.12)', border: '1px solid rgba(214,239,78,0.25)', color: '#D6EF4E' }}>
          Página {currentPage} de {totalPages || 1}
        </span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">Siguiente ›</button>
        <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">»</button>
      </div>
    </div>
  );
};

// ─── Default filters ──────────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  search: '',
  clienteId: 'all',
  empleadoId: 'all',
  productoId: 'all',
  tipo: 'all',
  estado: 'all',
  dateFrom: '',
  dateTo: '',
  quickFilter: 'week',
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const LoncheadosControlCenter = () => {
  const [loncheados, setLoncheados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);

  // Modals
  const [detailLoncheado, setDetailLoncheado] = useState(null);
  const [editLoncheado, setEditLoncheado] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  // KPIs
  const [rendKpis, setRendKpis] = useState({ rendimientoMedio: null, mejorRendimiento: null, peorRendimiento: null, kgProcesados: null });
  const [kpisLoading, setKpisLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Ref to track if filters changed (to reset page to 1)
  const filtersRef = useRef(filters);

  useEffect(() => {
    loadDropdownData();
    loadKPIs();
  }, []);

  // Reset to page 1 whenever filters or pageSize change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, pageSize]);

  // Load data whenever currentPage, pageSize, or filters change
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize, filters]);

  const loadDropdownData = async () => {
    try {
      const [clientesData, empleadosData, productosData] = await Promise.all([
        clientService?.getAll(),
        employeeService?.getAll(),
        productosService?.getAll()
      ]);
      setClientes(clientesData || []);
      setEmpleados(empleadosData || []);
      setProductos(productosData || []);
    } catch (err) {
      console.error('[LoncheadosControlCenter] Error en loadDropdownData:', err?.message || err);
    }
  };

  const loadKPIs = async () => {
    setKpisLoading(true);
    try {
      // Cargar todos los registros para KPIs globales (sin límite de paginación)
      const { data } = await supabase?.from('loncheados')?.select('peso_inicial, peso, rendimiento, peso_aprovechado');
      
      // Usar peso_inicial como fuente principal (NO peso_loncheado)
      const kgTotal = (data || [])?.reduce((s, l) => {
        const pi = parseFloat(l?.peso_inicial);
        return s + (isNaN(pi) || pi <= 0 ? 0 : pi);
      }, 0);

      // Rendimiento: normalizar registro por registro
      // Si rendimiento está entre 0-1 → es ratio → ×100; si 1-100 → usar directo
      // Excluir registros con pesos inconsistentes (peso_aprovechado > peso_inicial)
      const rendValues = (data || [])?.reduce((acc, l) => {
        const pi = parseFloat(l?.peso_inicial);
        const pa = parseFloat(l?.peso_aprovechado);
        // Calcular desde pesos si disponibles y consistentes
        if (!isNaN(pi) && pi > 0 && !isNaN(pa) && pa >= 0 && pa <= pi) {
          acc?.push((pa / pi) * 100);
          return acc;
        }
        // Fallback: usar campo rendimiento almacenado
        const r = parseFloat(l?.rendimiento);
        if (!isNaN(r) && r > 0) {
          const rNorm = r <= 1 ? r * 100 : r;
          if (rNorm >= 0 && rNorm <= 100) acc?.push(rNorm);
        }
        return acc;
      }, []);

      setRendKpis({
        rendimientoMedio: rendValues?.length > 0 ? rendValues?.reduce((a, b) => a + b, 0) / rendValues?.length : null,
        mejorRendimiento: rendValues?.length > 0 ? Math.max(...rendValues) : null,
        peorRendimiento: rendValues?.length > 0 ? Math.min(...rendValues) : null,
        kgProcesados: kgTotal,
      });
    } catch { /* silent */ } finally {
      setKpisLoading(false);
    }
  };

  /**
   * Build the Supabase query applying ALL filters with AND logic.
   * All date calculations use Europe/Madrid timezone via dateRangeHelper.
   * Manual dates take priority over quick period when both are set.
   */
  const buildQuery = useCallback((forCount = false) => {
    let query = supabase?.from('loncheados')?.select('*', { count: 'exact' });

    // ── ID-based filters (AND logic) ──────────────────────────────────────────
    if (filters?.clienteId && filters?.clienteId !== 'all') {
      query = query?.eq('cliente_id', filters?.clienteId);
    }
    if (filters?.empleadoId && filters?.empleadoId !== 'all') {
      query = query?.eq('empleado_id', filters?.empleadoId);
    }
    if (filters?.productoId && filters?.productoId !== 'all') {
      query = query?.eq('producto_id', filters?.productoId);
    }
    if (filters?.tipo && filters?.tipo !== 'all') {
      query = query?.eq('tipo', filters?.tipo);
    }

    // ── Estado filter in Supabase ─────────────────────────────────────────────
    if (filters?.estado && filters?.estado !== 'all') {
      if (filters?.estado === 'excelente') {
        query = query?.gt('rendimiento', 50);
      } else if (filters?.estado === 'bueno') {
        query = query?.gte('rendimiento', 45)?.lte('rendimiento', 50);
      } else if (filters?.estado === 'revisar') {
        query = query?.gte('rendimiento', 40)?.lt('rendimiento', 45);
      } else if (filters?.estado === 'bajo') {
        query = query?.lt('rendimiento', 40);
      }
    }

    // ── Date range ────────────────────────────────────────────────────────────
    // Manual dates (dateFrom / dateTo) take full priority over quickFilter.
    // Uses dateRangeHelper: gte(start) + lt(nextDayStart) for correct full-day inclusion.
    const hasManualFrom = filters?.dateFrom && filters?.dateFrom !== '';
    const hasManualTo = filters?.dateTo && filters?.dateTo !== '';

    if (hasManualFrom || hasManualTo) {
      query = applyCustomDateRange(query, filters?.dateFrom, filters?.dateTo, 'created_at');
    } else if (filters?.quickFilter && filters?.quickFilter !== '') {
      query = applyQuickPeriodFilter(query, filters?.quickFilter, 'created_at');
    }

    // ── Text search ───────────────────────────────────────────────────────────
    if (filters?.search && filters?.search?.trim() !== '') {
      const s = filters?.search?.trim();
      query = query?.or(
        `lote_interno.ilike.%${s}%,lote_externo.ilike.%${s}%`
      );
    }

    return query;
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * pageSize;

      // Use Supabase join to fetch cliente, empleado, producto names directly
      // This ensures names are always resolved even if dropdown arrays are empty
      let query = supabase
        ?.from('loncheados')
        ?.select(`
          *,
          cliente:clientes(id, nombre),
          empleado:empleados(id, nombre),
          producto:productos(id, nombre),
          plan_trabajo:plan_trabajo(id, fecha, estado, cantidad_piezas, cliente:clientes(nombre))
        `, { count: 'exact' });

      // ── ID-based filters (AND logic) ──────────────────────────────────────────
      if (filters?.clienteId && filters?.clienteId !== 'all') {
        query = query?.eq('cliente_id', filters?.clienteId);
      }
      if (filters?.empleadoId && filters?.empleadoId !== 'all') {
        query = query?.eq('empleado_id', filters?.empleadoId);
      }
      if (filters?.productoId && filters?.productoId !== 'all') {
        query = query?.eq('producto_id', filters?.productoId);
      }
      if (filters?.tipo && filters?.tipo !== 'all') {
        query = query?.eq('tipo', filters?.tipo);
      }

      // ── Estado filter ─────────────────────────────────────────────────────────
      if (filters?.estado && filters?.estado !== 'all') {
        if (filters?.estado === 'excelente') {
          query = query?.gt('rendimiento', 50);
        } else if (filters?.estado === 'bueno') {
          query = query?.gte('rendimiento', 45)?.lte('rendimiento', 50);
        } else if (filters?.estado === 'revisar') {
          query = query?.gte('rendimiento', 40)?.lt('rendimiento', 45);
        } else if (filters?.estado === 'bajo') {
          query = query?.lt('rendimiento', 40);
        }
      }

      // ── Date range ────────────────────────────────────────────────────────────
      const hasManualFrom = filters?.dateFrom && filters?.dateFrom !== '';
      const hasManualTo = filters?.dateTo && filters?.dateTo !== '';

      if (hasManualFrom || hasManualTo) {
        query = applyCustomDateRange(query, filters?.dateFrom, filters?.dateTo, 'created_at');
      } else if (filters?.quickFilter && filters?.quickFilter !== '') {
        query = applyQuickPeriodFilter(query, filters?.quickFilter, 'created_at');
      }

      // ── Text search ───────────────────────────────────────────────────────────
      if (filters?.search && filters?.search?.trim() !== '') {
        const s = filters?.search?.trim();
        query = query?.or(
          `lote_interno.ilike.%${s}%,lote_externo.ilike.%${s}%`
        );
      }

      query = query?.order('created_at', { ascending: false })?.range(offset, offset + pageSize - 1);

      const { data, count, error: qErr } = await query;
      if (qErr) throw qErr;

      let filtered = data || [];

      // ── Client-side search for cliente/empleado/producto names ───────────────
      if (filters?.search && filters?.search?.trim() !== '') {
        const s = filters?.search?.trim()?.toLowerCase();
        const matchingClientIds = clientes?.filter(c => c?.nombre?.toLowerCase()?.includes(s))?.map(c => String(c?.id));
        const matchingEmpIdsFixed = empleados?.filter(e => e?.nombre?.toLowerCase()?.includes(s))?.map(e => String(e?.id));
        const matchingProdIds = productos?.filter(p => p?.nombre?.toLowerCase()?.includes(s))?.map(p => String(p?.id));

        const hasNameMatch = matchingClientIds?.length > 0 || matchingEmpIdsFixed?.length > 0 || matchingProdIds?.length > 0;

        if (hasNameMatch) {
          let broadQuery = supabase
            ?.from('loncheados')
            ?.select(`
              *,
              cliente:clientes(id, nombre),
              empleado:empleados(id, nombre),
              producto:productos(id, nombre)
            `, { count: 'exact' });

          if (filters?.clienteId && filters?.clienteId !== 'all') broadQuery = broadQuery?.eq('cliente_id', filters?.clienteId);
          if (filters?.empleadoId && filters?.empleadoId !== 'all') broadQuery = broadQuery?.eq('empleado_id', filters?.empleadoId);
          if (filters?.productoId && filters?.productoId !== 'all') broadQuery = broadQuery?.eq('producto_id', filters?.productoId);
          if (filters?.tipo && filters?.tipo !== 'all') broadQuery = broadQuery?.eq('tipo', filters?.tipo);
          if (filters?.estado && filters?.estado !== 'all') {
            if (filters?.estado === 'excelente') broadQuery = broadQuery?.gt('rendimiento', 50);
            else if (filters?.estado === 'bueno') broadQuery = broadQuery?.gte('rendimiento', 45)?.lte('rendimiento', 50);
            else if (filters?.estado === 'revisar') broadQuery = broadQuery?.gte('rendimiento', 40)?.lt('rendimiento', 45);
            else if (filters?.estado === 'bajo') broadQuery = broadQuery?.lt('rendimiento', 40);
          }

          const hasManualFrom2 = filters?.dateFrom && filters?.dateFrom !== '';
          const hasManualTo2 = filters?.dateTo && filters?.dateTo !== '';
          if (hasManualFrom2 || hasManualTo2) {
            broadQuery = applyCustomDateRange(broadQuery, filters?.dateFrom, filters?.dateTo, 'created_at');
          } else if (filters?.quickFilter && filters?.quickFilter !== '') {
            broadQuery = applyQuickPeriodFilter(broadQuery, filters?.quickFilter, 'created_at');
          }

          broadQuery = broadQuery?.order('created_at', { ascending: false });
          const { data: broadData } = await broadQuery;

          const allFiltered = (broadData || [])?.filter(l => {
            const loteMatch =
              l?.lote_interno?.toLowerCase()?.includes(s) ||
              l?.lote_externo?.toLowerCase()?.includes(s);
            const nameMatch =
              matchingClientIds?.includes(String(l?.cliente_id)) ||
              matchingEmpIdsFixed?.includes(String(l?.empleado_id)) ||
              matchingProdIds?.includes(String(l?.producto_id));
            return loteMatch || nameMatch;
          });

          const totalFiltered = allFiltered?.length;
          const pageFiltered = allFiltered?.slice(offset, offset + pageSize);

          console.debug('[LoncheadosFilters] Search results (broad):', totalFiltered, 'records');

          const mapped = pageFiltered?.map(l => mapLoncheado(l));
          setLoncheados(mapped);
          setTotalRecords(totalFiltered);
          return;
        }
      }

      console.debug('[LoncheadosFilters] Records fetched:', count, '| page:', currentPage);

      const mapped = filtered?.map(l => mapLoncheado(l));
      setLoncheados(mapped);
      setTotalRecords(count || 0);
    } catch (err) {
      console.error('[LoncheadosControlCenter] Error en loadData:', err?.message || err);
      setError(err?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const mapLoncheado = (l) => ({
    id: l?.id,
    clienteId: l?.cliente_id,
    empleadoId: l?.empleado_id,
    productoId: l?.producto_id,
    // Use joined relation names directly (from Supabase join)
    clienteNombre: l?.cliente?.nombre || null,
    empleadoNombre: l?.empleado?.nombre || null,
    productoNombre: l?.producto?.nombre || null,
    pesoInicial: l?.peso_inicial || l?.peso,
    pesoAprovechado: l?.peso_aprovechado,
    merma: l?.merma,
    rendimiento: l?.rendimiento,
    tipo: l?.tipo,
    observaciones: l?.observaciones,
    fotoUrl: l?.foto_url,
    foto_url: l?.foto_url,
    createdAt: l?.created_at,
    created_at: l?.created_at,
    estado_rendimiento: l?.estado_rendimiento,
    estadoRendimiento: l?.estado_rendimiento,
    loteInterno: l?.lote_interno,
    loteExterno: l?.lote_externo,
    lote_interno: l?.lote_interno,
    lote_externo: l?.lote_externo,
    emplatado: l?.emplatado,
    numeroBlisteres: l?.numero_blisteres,
    numero_blisteres: l?.numero_blisteres,
    tarifa: l?.tarifa,
    coste_sala: l?.coste_sala,
    cliente_id: l?.cliente_id,
    empleado_id: l?.empleado_id,
    producto_id: l?.producto_id,
    peso: l?.peso,
    peso_inicial: l?.peso_inicial,
    peso_aprovechado: l?.peso_aprovechado,
    peso_loncheado: l?.peso_loncheado,
    // Plan de Trabajo context
    planTrabajoId: l?.plan_trabajo_id || null,
    planTrabajoPiezaId: l?.plan_trabajo_pieza_id || null,
    planTrabajo: l?.plan_trabajo || null,
  });

  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({ ...DEFAULT_FILTERS });
    } else if (key === 'quickFilter') {
      // Activating a quick filter clears manual date range
      setFilters(prev => ({ ...prev, quickFilter: value, dateFrom: '', dateTo: '' }));
    } else if (key === 'dateFrom' || key === 'dateTo') {
      // Entering a manual date deactivates the quick filter
      setFilters(prev => ({ ...prev, [key]: value, quickFilter: '' }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este registro?')) return;
    try {
      setLoading(true);
      await supabase?.from('salarios')?.delete()?.eq('loncheado_id', id);
      const { error: delErr } = await supabase?.from('loncheados')?.delete()?.eq('id', id);
      if (delErr) throw delErr;
      await loadData();
      if (detailLoncheado?.id === id) setDetailLoncheado(null);
      if (editLoncheado?.id === id) setEditLoncheado(null);
    } catch (err) {
      setError(err?.message || 'Error al eliminar registro');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSaved = async () => {
    await loadData();
    await loadKPIs();
  };

  const handleExportPDF = async () => {
    try {
      setBtnLoading(true);
      await exportLoncheadosPDF({ filteredLoncheados: loncheados, clientes, empleados, productos, filters });
    } catch { setError('Error al generar el PDF.'); } finally { setBtnLoading(false); }
  };

  const handleExportExcel = () => {
    setBtnLoading(true);
    const exportData = loncheados?.map(l => {
      const clienteNombre = l?.clienteNombre || clientes?.find(c => c?.id === l?.clienteId)?.nombre || '';
      const empleadoNombre = l?.empleadoNombre || empleados?.find(e => e?.id === l?.empleadoId)?.nombre || '';
      const productoNombre = l?.productoNombre || productos?.find(p => p?.id === l?.productoId)?.nombre || '';
      return {
        'Fecha': new Date(l?.createdAt)?.toLocaleString('es-ES'),
        'Cliente': clienteNombre,
        'Empleado': empleadoNombre,
        'Producto': productoNombre,
        'Tipo': l?.tipo || '',
        'Peso Inicial (kg)': l?.pesoInicial != null ? parseFloat(l?.pesoInicial)?.toFixed(2) : '',
        'Peso Aprovechado (kg)': l?.pesoAprovechado != null ? parseFloat(l?.pesoAprovechado)?.toFixed(2) : '',
        'Merma (kg)': l?.merma != null ? parseFloat(l?.merma)?.toFixed(2) : '',
        'Rendimiento (%)': l?.rendimiento != null ? parseFloat(l?.rendimiento)?.toFixed(1) : '',
        'Estado': l?.estadoRendimiento || '',
        'Lote Interno': l?.loteInterno || '',
        'Lote Externo': l?.loteExterno || '',
        'Observaciones': l?.observaciones || '',
      };
    });
    const worksheet = XLSX?.utils?.json_to_sheet(exportData);
    const workbook = XLSX?.utils?.book_new();
    XLSX?.utils?.book_append_sheet(workbook, worksheet, 'Loncheados');
    XLSX?.writeFile(workbook, `loncheados-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
    setBtnLoading(false);
  };

  const getRendColor = (val) => {
    if (val == null) return '#ffffff';
    if (val > 50) return '#3A7F6B';
    if (val >= 45) return '#5aab8e';
    if (val >= 40) return '#C9A24D';
    return '#E05252';
  };

  const kpiCards = [
    {
      title: 'Rendimiento Medio',
      value: rendKpis?.rendimientoMedio != null ? `${rendKpis?.rendimientoMedio?.toFixed(1)}%` : '—',
      subtitle: rendKpis?.rendimientoMedio != null
        ? (rendKpis?.rendimientoMedio > 50 ? '🟢 Excelente' : rendKpis?.rendimientoMedio >= 45 ? '🟢 Bueno' : rendKpis?.rendimientoMedio >= 40 ? '🟡 Revisar' : '🔴 Bajo')
        : 'Sin datos',
      icon: 'BarChart3',
      iconColor: rendKpis?.rendimientoMedio != null ? getRendColor(rendKpis?.rendimientoMedio) : '#C9A24D',
      valueColor: rendKpis?.rendimientoMedio != null ? getRendColor(rendKpis?.rendimientoMedio) : '#ffffff',
    },
    {
      title: 'Mejor Rendimiento',
      value: rendKpis?.mejorRendimiento != null ? `${rendKpis?.mejorRendimiento?.toFixed(1)}%` : '—',
      subtitle: 'Máximo registrado',
      icon: 'TrendingUp',
      iconColor: '#3A7F6B',
      valueColor: '#3A7F6B',
    },
    {
      title: 'Peor Rendimiento',
      value: rendKpis?.peorRendimiento != null ? `${rendKpis?.peorRendimiento?.toFixed(1)}%` : '—',
      subtitle: 'Mínimo registrado',
      icon: 'TrendingDown',
      iconColor: '#E05252',
      valueColor: '#E05252',
    },
    {
      title: 'Kg Procesados',
      value: rendKpis?.kgProcesados != null ? `${rendKpis?.kgProcesados?.toFixed(1)} kg` : '—',
      subtitle: 'Peso total procesado',
      icon: 'Scale',
      iconColor: '#C9A24D',
      valueColor: '#C9A24D',
    },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <>
      <Helmet>
        <title>Control de Loncheados - Las Dos Doncellas</title>
      </Helmet>

      <ERPPage>
        {/* Header */}
        <ERPPageHeader
          title="Control de Loncheados"
          subtitle="Visualización y gestión completa de todos los registros de loncheado"
          actions={
            <>
              <ERPButton variant="ghost" icon="FileText" onClick={handleExportPDF} loading={btnLoading} disabled={btnLoading}>
                Informe PDF
              </ERPButton>
              <ERPButton variant="secondary" icon="FileDown" onClick={handleExportExcel} loading={btnLoading} disabled={btnLoading}>
                Excel
              </ERPButton>
            </>
          }
        />

        {/* KPI Cards */}
        <ERPKPIGrid cols={4}>
          {kpiCards?.map(k => (
            <ERPKPICard key={k?.title} {...k} loading={kpisLoading} />
          ))}
        </ERPKPIGrid>

        {/* Filter Bar */}
        <div
          className="rounded-xl px-4 py-3 mb-5 flex flex-wrap items-center gap-3"
          style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.3)' }} />
            <input
              type="text"
              placeholder="Buscar cliente, empleado, producto, lote..."
              value={filters?.search}
              onChange={e => handleFilterChange('search', e?.target?.value)}
              className="w-full text-sm rounded-lg pl-8 pr-3 py-2 font-oranienbaum focus:outline-none"
              style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' }}
            />
          </div>

          {/* Quick filters */}
          <div className="flex items-center gap-1">
            {[{ key: 'today', label: 'Hoy' }, { key: 'week', label: 'Semana' }, { key: 'month', label: 'Mes' }, { key: 'year', label: 'Año' }]?.map(qf => (
              <button
                key={qf?.key}
                onClick={() => handleFilterChange('quickFilter', filters?.quickFilter === qf?.key ? '' : qf?.key)}
                className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-colors"
                style={filters?.quickFilter === qf?.key && !filters?.dateFrom && !filters?.dateTo
                  ? { background: 'rgba(214,239,78,0.15)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.30)' }
                  : { color: 'rgba(255,255,255,0.50)', border: '1px solid transparent' }
                }
              >
                {qf?.label}
              </button>
            ))}
          </div>

          {/* Cliente */}
          <select
            value={filters?.clienteId}
            onChange={e => handleFilterChange('clienteId', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none min-w-[140px]"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          >
            <option value="all">Todos los clientes</option>
            {clientes?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}</option>)}
          </select>

          {/* Empleado */}
          <select
            value={filters?.empleadoId}
            onChange={e => handleFilterChange('empleadoId', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none min-w-[140px]"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          >
            <option value="all">Todos los empleados</option>
            {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
          </select>

          {/* Tipo */}
          <select
            value={filters?.tipo}
            onChange={e => handleFilterChange('tipo', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none min-w-[130px]"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          >
            <option value="all">Todos los tipos</option>
            <option value="NORMAL">Normal</option>
            <option value="EMPLATADO">Emplatado</option>
            <option value="GRANDE_Y_EMPLATADO">Grande y Emplatado</option>
            <option value="PALETILLA">Paletilla</option>
          </select>

          {/* Estado */}
          <select
            value={filters?.estado}
            onChange={e => handleFilterChange('estado', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none min-w-[130px]"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          >
            <option value="all">Todos los estados</option>
            <option value="excelente">Excelente (&gt;50%)</option>
            <option value="bueno">Bueno (45-50%)</option>
            <option value="revisar">Revisar (40-45%)</option>
            <option value="bajo">Bajo (&lt;40%)</option>
          </select>

          {/* Fecha desde */}
          <input
            type="date"
            value={filters?.dateFrom}
            onChange={e => handleFilterChange('dateFrom', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          />
          {/* Fecha hasta */}
          <input
            type="date"
            value={filters?.dateTo}
            onChange={e => handleFilterChange('dateTo', e?.target?.value)}
            className="text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}
          />

          {/* Limpiar */}
          <button
            onClick={() => handleFilterChange('reset', null)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-oranienbaum transition-all"
            style={{ color: 'rgba(255,255,255,0.50)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Icon name="X" size={12} />
            Limpiar
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl flex items-start gap-3"
            style={{ background: 'rgba(224,82,82,0.10)', border: '1px solid rgba(224,82,82,0.30)' }}>
            <Icon name="AlertCircle" size={16} style={{ color: '#E05252', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <p className="font-oranienbaum text-sm font-bold" style={{ color: '#E05252' }}>Error</p>
              <p className="font-oranienbaum text-xs mt-0.5" style={{ color: 'rgba(224,82,82,0.8)' }}>{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-white/30 hover:text-white/60">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Table Card */}
        <div
          className="rounded-xl overflow-hidden"
          style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" size={32} className="animate-spin" style={{ color: '#D6EF4E' }} />
            </div>
          ) : loncheados?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Icon name="Package" size={40} style={{ color: 'rgba(255,255,255,0.15)' }} />
              <p className="font-oranienbaum text-base" style={{ color: 'rgba(255,255,255,0.35)' }}>
                No se encontraron registros con los filtros aplicados
              </p>
              <button
                onClick={() => handleFilterChange('reset', null)}
                className="text-xs font-oranienbaum px-3 py-1.5 rounded-lg transition-all"
                style={{ color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.25)' }}
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <>
              <LoncheadosTable
                loncheados={loncheados}
                clientes={clientes}
                empleados={empleados}
                productos={productos}
                onEdit={setEditLoncheado}
                onDelete={handleDelete}
                onVerDetalle={setDetailLoncheado}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={s => { setPageSize(s); setCurrentPage(1); }}
              />
            </>
          )}
        </div>
      </ERPPage>

      {/* Detail Modal */}
      {detailLoncheado && (
        <LoncheadosDetailModal
          loncheado={detailLoncheado}
          clientes={clientes}
          empleados={empleados}
          productos={productos}
          onClose={() => setDetailLoncheado(null)}
          onEdit={(l) => { setDetailLoncheado(null); setEditLoncheado(l); }}
        />
      )}

      {/* Edit Modal */}
      {editLoncheado && (
        <LoncheadosEditModal
          loncheado={editLoncheado}
          clientes={clientes}
          empleados={empleados}
          productos={productos}
          onClose={() => setEditLoncheado(null)}
          onSaved={handleEditSaved}
        />
      )}
    </>
  );
};

export default LoncheadosControlCenter;